import { mq } from "@/constants/breakpoints";
import { darkColor } from "@/constants/colors";
import { gotham, inter } from "@/utils/fonts";
import { css } from "@emotion/react";
import Link from "next/link";
import SpecialOfferBadge from "./SpecialOfferBadge";
import Button from "../Buttons/Button";
import { H6Css, Heading } from "../Typography";

interface NewPricingCardProps {
  pricingPlan?: {
    name: string;
    description?: string;
    monthlyPrice: number;
    annualPrice: number;
    annualPricePerMonth: number;
    buttonLabel: string;
    disabledButtonLabel: string;
    buttonRelativeLink: string;
    specialOffer?: {
      textBeforeAmount?: string;
      amountText: string;
      textAfterAmount?: string;
      amountColor?: string;
    };
  };
  isAnnual: boolean;
  disabled?: boolean;
  position?: "first" | "middle" | "last";
}

const NewPricingCard = ({
  pricingPlan,
  isAnnual,
  disabled = false,
  position,
}: NewPricingCardProps) => {
  // Fallback values if no pricingPlan is provided
  const label = pricingPlan?.name || "Basic";
  const price = isAnnual
    ? (pricingPlan?.annualPricePerMonth || 29).toString()
    : (pricingPlan?.monthlyPrice || 44).toString();
  const buttonLabel = disabled
    ? pricingPlan?.disabledButtonLabel
    : pricingPlan?.buttonLabel || "Get Started";
  const buttonLink = pricingPlan?.buttonRelativeLink || "/sign-up";

  // Create tracking ID for PostHog analytics
  const planName = label.toLowerCase().replace(/\s+/g, '-');
  const billingFrequency = isAnnual ? 'annual' : 'monthly';
  const trackingId = `try-for-free-${planName}-${billingFrequency}`;

  // Border radius for outer corners only
  let borderRadius = "0";
  if (position === "first") borderRadius = "24px 24px 0 0";
  if (position === "last") borderRadius = "0 0 24px 24px";
  if (position === "first" && mq["lg"]) borderRadius = "24px 0 0 24px";
  if (position === "last" && mq["lg"]) borderRadius = "0 24px 24px 0";

  return (
    <div
      className={gotham.className}
      css={css`
        border: none;
        border-radius: ${borderRadius};
        box-shadow: none;
        padding: 30px 40px;
        text-align: center;
        background-color: #fff;
        max-width: 370px;
        position: relative;
        opacity: ${disabled ? 0.5 : 1};
        pointer-events: ${disabled ? "none" : "auto"};
        display: flex;
        flex-grow: 1;
        flex-direction: column;
        align-items: center;
        margin: 0;
        width: 100%;
        min-width: 0;
        word-break: break-word;
        white-space: normal;
        box-sizing: border-box;
        ${mq["lg"]} {
          padding: 40px 24px;
          max-width: 370px;
        }
      `}
    >
      <Heading
        level={5}
        className={gotham.className}
        css={css`
          color: ${darkColor};
          margin-bottom: 8px;
          ${H6Css}
        `}
      >
        {label}
      </Heading>
      <div
        css={css`
          display: flex;
          width: fit-content;
          margin: auto;
          align-items: flex-end;
        `}
      >
        <Heading
          level={6}
          className={gotham.className}
          css={css`
            font-size: 50px;
            font-weight: 400;
            line-height: 1;
            color: ${darkColor};
            margin-right: 4px;
            line-height: 130.023%;
          `}
        >
          ${price}
        </Heading>
        <span
          className={gotham.className}
          css={css`
            color: ${darkColor};
            font-size: 14px;
            font-weight: 350;
            margin-bottom: 13px;
            white-space: nowrap;
          `}
        >
          / month
        </span>
      </div>
      {isAnnual && (
        <Heading
          className={gotham.className}
          css={css`
            color: #10c3c3;
            margin-top: 16px;
            font-size: 14px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
          `}
        >
          When Paid Annually
        </Heading>
      )}
      {pricingPlan?.specialOffer && (
        <div
          css={css`
            margin: 18px 0 10px 0;
            width: 100%;
            display: flex;
            justify-content: center;
          `}
        >
          <SpecialOfferBadge specialOffer={pricingPlan.specialOffer} />
        </div>
      )}
      <div
        className={inter.className}
        css={css`
          color: #657197;
          font-size: 14px;
          font-weight: 400;
          min-height: 40px;
          word-break: break-word;
          white-space: normal;
          margin-top: 24px;
          ${mq["lg"]} {
            margin-top: 30px;
          }
        `}
      >
        {pricingPlan?.description}
      </div>
      <div
        css={css`
          margin-top: 24px;
          ${mq["lg"]} {
            margin-top: 30px;
          }
          width: 100%;
        `}
      >
        <Link href={buttonLink} passHref legacyBehavior>
          <a
            style={{ width: "100%", display: "block", textDecoration: "none" }}
          >
            <Button
              jwType="lightBlue"
              fullWidth
              disabled={disabled}
              id={trackingId}
              css={css`
                font-size: 18px;
                font-weight: 500;
                border-radius: 10px;
                height: 56px;
                @media (max-width: 600px) {
                  font-size: 15px;
                  padding: 12px 0;
                  height: 44px;
                }
              `}
            >
              {buttonLabel}
            </Button>
          </a>
        </Link>
      </div>
    </div>
  );
};

export default NewPricingCard;
